import axios from '../utils/axios';

class ModelComparisonService {
  /**
   * Get all model comparisons for the authenticated user
   */
  async getComparisons(params = {}) {
    try {
      const response = await axios.get('/model-comparisons', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get a specific model comparison by ID
   */
  async getComparison(id) {
    try {
      const response = await axios.get(`/model-comparisons/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new model comparison
   */
  async createComparison(data) {
    try {
      const response = await axios.post('/model-comparisons', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update an existing model comparison
   */
  async updateComparison(id, data) {
    try {
      const response = await axios.put(`/model-comparisons/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a model comparison
   */
  async deleteComparison(id) {
    try {
      const response = await axios.delete(`/model-comparisons/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Execute a model comparison
   */
  async executeComparison(id) {
    try {
      const response = await axios.post(`/model-comparisons/${id}/execute`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get comparison results
   */
  async getComparisonResults(id) {
    try {
      const response = await axios.get(`/model-comparisons/${id}/results`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get full audit logs timeline for a comparison (paginated)
   */
  async getComparisonAuditLogs(id, params = {}) {
    try {
      const response = await axios.get(`/model-comparisons/${id}/audit-logs`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get comparison statistics
   */
  async getStats() {
    try {
      const response = await axios.get('/model-comparisons/stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Clone a model comparison
   */
  async cloneComparison(id, name) {
    try {
      const response = await axios.post(`/model-comparisons/${id}/clone`, { name });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Export comparison results
   */
  async exportComparison(id, format = 'csv') {
    try {
      const response = await axios.get(`/model-comparisons/${id}/export`, {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || 'An error occurred';
      const errors = error.response.data?.errors || {};
      const data = error.response.data?.data || null;
      const status = error.response.status;
      return { message, errors, data, status };
    } else if (error.request) {
      // Request was made but no response received
      return { message: 'Network error - please check your connection', errors: {}, data: null };
    } else {
      // Something else happened
      return { message: error.message || 'An unexpected error occurred', errors: {}, data: null };
    }
  }

  /**
   * Get available providers
   */
  getProviders() {
    // Base presets
    const base = {
      openai: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'],
      anthropic: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'],
      google: ['gemini-pro', 'gemini-pro-vision'],
      groq: ['llama2-70b-4096', 'mixtral-8x7b-32768'],
      custom: [],
    };

    // Merge in user-configured models from API Configuration page (localStorage)
    try {
      const saved = localStorage.getItem('llm-configurations');
      const configs = saved ? JSON.parse(saved) : [];
      const byProvider = {};

      configs.forEach((cfg) => {
        const provider = cfg?.provider;
        if (!provider) return;
        const isActive = cfg?.is_active !== false; // default active when missing
        if (!isActive) return;
        const conf = cfg?.configuration || {};
        const list = [];
        if (conf.default_model && typeof conf.default_model === 'string') list.push(conf.default_model.trim());
        if (conf.custom_models && typeof conf.custom_models === 'string') {
          conf.custom_models
            .split(/\r?\n|,/)
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((m) => list.push(m));
        }
        if (!byProvider[provider]) byProvider[provider] = [];
        byProvider[provider].push(...list);
      });

      Object.entries(byProvider).forEach(([provider, models]) => {
        const existing = base[provider] || [];
        const combined = Array.from(new Set([...(existing || []), ...models.filter(Boolean)]));
        base[provider] = combined;
      });
    } catch (_) {
      // Ignore parsing errors; fall back to base presets
    }

    return [
      { value: 'openai', label: 'OpenAI', models: base.openai },
      { value: 'anthropic', label: 'Anthropic', models: base.anthropic },
      { value: 'google', label: 'Google', models: base.google },
      { value: 'groq', label: 'Groq', models: base.groq },
      { value: 'custom', label: 'Custom Provider', models: base.custom },
    ];
  }

  /**
   * Get available metrics
   */
  getMetrics() {
    return [
      { value: 'accuracy', label: 'Accuracy', description: 'Correctness of responses' },
      { value: 'latency', label: 'Response Time', description: 'Time taken to generate response' },
      { value: 'cost', label: 'Cost', description: 'API usage cost' },
      { value: 'quality', label: 'Quality Score', description: 'Overall response quality' }
    ];
  }

  /**
   * Get available templates
   */
  getTemplates() {
    return [
      { value: 'coding', label: 'Coding Assessment', description: 'Test programming capabilities' },
      { value: 'reasoning', label: 'Logical Reasoning', description: 'Test logical thinking' },
      { value: 'creative', label: 'Creative Writing', description: 'Test creative abilities' },
      { value: 'qa', label: 'Question Answering', description: 'Test knowledge and comprehension' },
      { value: 'summarization', label: 'Text Summarization', description: 'Test summarization skills' },
      { value: 'translation', label: 'Language Translation', description: 'Test translation accuracy' }
    ];
  }
}

const modelComparisonService = new ModelComparisonService();
export default modelComparisonService;
